import { createSelector } from 'reselect';
import { select } from 'typed-redux-saga/macro';

import {
  BoundingPolyRect,
  DetectionGap,
} from '@common-modules/mediaDetails/models';

import {
  selectAutoInterpolationMax,
  selectManualInterpolationMax,
  selectInterpolationMaxStretch,
  selectStore,
} from './view';
import { WorkerState } from '../models';

const selectDetectionGaps = createSelector(selectStore, (s) => s.detectionGaps);

export function filterAcceptableAutoGaps(
  groupIds: Array<string>,
  gaps: WorkerState['detectionGaps'],
  autoInterpolationMax: number,
  interpolationMaxStretch: number | undefined
) {
  const autoGaps: {
    [groupId: string]: WorkerState['detectionGaps']['groupId'];
  } = {};
  groupIds.forEach((groupId) => {
    const groupGaps = gaps[groupId];
    const filteredClusterGaps: WorkerState['detectionGaps']['groupId'] = {};
    if (groupGaps) {
      Object.values(groupGaps).forEach((gap) => {
        if (
          gap &&
          gap.gapLenMs <= autoInterpolationMax &&
          gapSizeAcceptable(gap, interpolationMaxStretch)
        ) {
          filteredClusterGaps[gap.id] = gap;
        }
      });

      if (Object.keys(filteredClusterGaps).length) {
        autoGaps[groupId] = filteredClusterGaps;
      }
    }
  });

  return autoGaps;
}

export function* selectDetectionGapsAutoSubset(groupIds: Array<string>) {
  const gaps = yield* select(selectDetectionGaps);
  const autoInterpolationMax = yield* select(selectAutoInterpolationMax);
  const interpolationMaxStretch = yield* select(selectInterpolationMaxStretch);
  return filterAcceptableAutoGaps(
    groupIds,
    gaps,
    autoInterpolationMax,
    interpolationMaxStretch
  );
}

export const selectDetectionGapsAuto = (groupIds?: Array<string>) =>
  createSelector(
    selectAutoInterpolationMax,
    selectInterpolationMaxStretch,
    selectDetectionGaps,
    (autoInterpolationMax, interpolationMaxStretch, gaps) =>
      filterAcceptableAutoGaps(
        groupIds || Object.keys(gaps),
        gaps,
        autoInterpolationMax,
        interpolationMaxStretch
      )
  );

export function filterAcceptableManualGaps(
  groupIds: Array<string>,
  gaps: WorkerState['detectionGaps'],
  manualInterpolationMax: number,
  autoInterpolationMax: number,
  interpolationMaxStretch: number | undefined
) {
  const manualGaps: {
    [groupId: string]: WorkerState['detectionGaps']['groupId'];
  } = {};
  groupIds.forEach((groupId) => {
    const groupGaps = gaps[groupId];
    const filteredClusterGaps: WorkerState['detectionGaps']['groupId'] = {};
    if (groupGaps) {
      Object.values(groupGaps).forEach((gap) => {
        if (
          gap &&
          gap.gapLenMs > autoInterpolationMax &&
          gap.gapLenMs <= manualInterpolationMax &&
          gapSizeAcceptable(gap, interpolationMaxStretch, 0.15) // legacy check for area
        ) {
          filteredClusterGaps[gap.id] = gap;
        }
      });

      if (Object.keys(filteredClusterGaps).length) {
        manualGaps[groupId] = filteredClusterGaps;
      }
    }
  });
  return manualGaps;
}

export function* selectDetectionGapsManualSubset(groupIds: Array<string>) {
  const gaps = yield* select(selectDetectionGaps);
  const manualInterpolationMax = yield* select(selectManualInterpolationMax);
  const autoInterpolationMax = yield* select(selectAutoInterpolationMax);
  const interpolationMaxStretch = yield* select(selectInterpolationMaxStretch);
  return filterAcceptableManualGaps(
    groupIds,
    gaps,
    manualInterpolationMax,
    autoInterpolationMax,
    interpolationMaxStretch
  );
}

export const selectDetectionGapsManual = (groupIds?: Array<string>) =>
  createSelector(
    selectAutoInterpolationMax,
    selectManualInterpolationMax,
    selectInterpolationMaxStretch,
    selectDetectionGaps,
    (
      autoInterpolationMax,
      manualInterpolationMax,
      interpolationMaxStretch,
      gaps
    ) =>
      filterAcceptableManualGaps(
        groupIds || Object.keys(gaps),
        gaps,
        manualInterpolationMax,
        autoInterpolationMax,
        interpolationMaxStretch
      )
  );

const gapArea = (gap: DetectionGap): number => {
  const polys = maxBoundingPoly(gap);
  return (polys[1].x - polys[0].x) * (polys[3].y - polys[1].y);
};

export const maxBoundingPoly = ({
  leftObject,
  rightObject,
}: DetectionGap): BoundingPolyRect => {
  const lo = leftObject.object;
  const ro = rightObject.object;

  return [
    {
      x: Math.min(lo.boundingPoly[0].x, ro.boundingPoly[0].x),
      y: Math.min(lo.boundingPoly[0].y, ro.boundingPoly[0].y),
    },
    {
      x: Math.max(lo.boundingPoly[1].x, ro.boundingPoly[1].x),
      y: Math.min(lo.boundingPoly[1].y, ro.boundingPoly[1].y),
    },
    {
      x: Math.min(lo.boundingPoly[2].x, ro.boundingPoly[2].x),
      y: Math.max(lo.boundingPoly[2].y, ro.boundingPoly[2].y),
    },
    {
      x: Math.max(lo.boundingPoly[3].x, ro.boundingPoly[3].x),
      y: Math.max(lo.boundingPoly[3].y, ro.boundingPoly[3].y),
    },
  ];
};

const gapSizeAcceptable = (
  gap: DetectionGap,
  interpolationMaxStretch: number | undefined,
  maxGapArea?: number
): boolean => {
  // only enforce maxGapArea as a legacy check
  if (interpolationMaxStretch === undefined) {
    if (maxGapArea) {
      return gapArea(gap) < maxGapArea;
    } else {
      return true;
    }
  }

  const lo = gap.leftObject.object;
  const ro = gap.rightObject.object;

  const gapWidth =
    Math.max(lo.boundingPoly[1].x, ro.boundingPoly[1].x) -
    Math.min(lo.boundingPoly[0].x, ro.boundingPoly[0].x);
  const gapHeight =
    Math.max(lo.boundingPoly[2].y, ro.boundingPoly[2].y) -
    Math.min(lo.boundingPoly[0].y, ro.boundingPoly[0].y);

  const loWidth = lo.boundingPoly[1].x - lo.boundingPoly[0].x;
  const loHeight = lo.boundingPoly[2].y - lo.boundingPoly[0].y;

  const roWidth = ro.boundingPoly[1].x - ro.boundingPoly[0].x;
  const roHeight = ro.boundingPoly[2].y - ro.boundingPoly[0].y;

  // keep small size gap objects - hardcode for now to simplify code
  if (Math.max(gapWidth, gapHeight) < 0.15) {
    return true;
  }

  // for larger gaps check they aren't "stretching" too much relative to the left/right object sizes
  const xRatio = gapWidth / Math.min(loWidth, roWidth);
  const yRatio = gapHeight / Math.min(loHeight, roHeight);
  if (xRatio > interpolationMaxStretch || yRatio > interpolationMaxStretch) {
    return false;
  } else {
    return true;
  }
};
